/**
 * Service-Catalog API.
 *
 * Backend: admin-api auf api.it-sce.de unter `/api/admin/catalog/*`.
 * Endpoints (Stand 2026-05-05):
 *   GET   /admin/catalog/services        — Liste aller Services
 *   PATCH /admin/catalog/services/{id}   — Toggle Visibility/Active/Featured, DisplayOrder
 *   POST  /admin/catalog/sync            — Sync von ERPNext anstoßen
 *
 * Audit-Endpoint (`/admin/catalog/services/{id}/audit`) ist noch nicht
 * im Backend implementiert — siehe TServiceAuditEntry. Das Resource-Modul
 * exponiert die Methode bereits, Page muss Empty-State sauber rendern.
 */
import type { AxiosInstance } from 'axios'
import { request } from '../client'
import type {
  TServiceCatalogEntry,
  TServiceCatalogList,
  TServiceCatalogUpdateRequest,
  TServiceCatalogSyncResult,
  TServiceAuditEntry,
} from '../types/staff'

interface AdminEnvelope<T> {
  success: boolean
  data: T
  error?: string
}

export interface ServicesApi {
  list: () => Promise<TServiceCatalogList>
  update: (id: number, req: TServiceCatalogUpdateRequest) => Promise<TServiceCatalogEntry>
  triggerSync: () => Promise<TServiceCatalogSyncResult>
  audit: (id: number) => Promise<{ entries: TServiceAuditEntry[] }>
}

export function createServicesApi(api: AxiosInstance): ServicesApi {
  const base = '/admin/catalog'
  return {
    list: async () => {
      const env = await request<AdminEnvelope<TServiceCatalogEntry[]>>(api, {
        method: 'GET',
        url: `${base}/services`,
      })
      return { services: env.data ?? [] }
    },
    update: async (id, req) => {
      const env = await request<AdminEnvelope<TServiceCatalogEntry>>(api, {
        method: 'PATCH',
        url: `${base}/services/${id}`,
        data: req,
      })
      return env.data
    },
    triggerSync: async () => {
      const env = await request<AdminEnvelope<TServiceCatalogSyncResult>>(api, {
        method: 'POST',
        url: `${base}/sync`,
      })
      return env.data
    },
    audit: async (id) => {
      // Endpoint noch nicht implementiert — gracefully degradieren bei 404
      try {
        const env = await request<AdminEnvelope<TServiceAuditEntry[]>>(api, {
          method: 'GET',
          url: `${base}/services/${id}/audit`,
        })
        return { entries: env.data ?? [] }
      } catch (e: unknown) {
        const status = (e as { status?: number }).status
        if (status === 404 || status === 501) {
          return { entries: [] }
        }
        throw e
      }
    },
  }
}
